// SP-2 — TanStack Query hooks for the B3 Monthly Statements queues
// (/portfolio-admin/statements): the pending-review list, the returned/
// correction list, and the queue-row mutations (send to TPS / regenerate).
// Reads register under loanStatementsKeys; mutations act on a cross-loan
// queue row so the row's loanId rides the mutate vars — they invalidate the
// whole statement prefix, the loan's Monthly Cycle Cockpit, and loan HEALTH
// (a statement is a loan-state fact the tab badges/attention feed surface).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { loanStatementsApi } from "@/api/portfolioAdmin/loanStatementsApi";
import { invalidateLoanHealth } from "@/hooks/portfolioAdmin/invalidation";
import {
  loanStatementsKeys,
  monthlyCycleKeys,
} from "@/hooks/portfolioAdmin/loanStatementsKeys";

export function usePendingReviewStatements() {
  return useQuery({
    queryKey: loanStatementsKeys.pendingReview(),
    queryFn: () => loanStatementsApi.pendingReview(),
  });
}

export function useReturnedStatements() {
  return useQuery({
    queryKey: loanStatementsKeys.correctionQueue(),
    queryFn: () => loanStatementsApi.correctionQueue(),
  });
}

/** Shared stale-after-save fan-out for the queue mutations: every statement
 *  read (queues + per-loan lists + details), the affected loan's Monthly
 *  Cycle Cockpit, and its health. */
function invalidateStatementScope(
  qc: ReturnType<typeof useQueryClient>,
  loanId: string,
) {
  void qc.invalidateQueries({ queryKey: loanStatementsKeys.statementsPrefix() });
  void qc.invalidateQueries({ queryKey: monthlyCycleKeys.loanPrefix(loanId) });
  void invalidateLoanHealth(qc, loanId);
}

export function useSendStatementToTpsFromQueue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { statementId: string; loanId: string }) =>
      loanStatementsApi.sendToTps(vars.statementId),
    onSuccess: (_data, vars) => invalidateStatementScope(qc, vars.loanId),
  });
}

/** WS3 — the review-screen payload (lines + provenance + delta + thread). */
export function useStatementReview(statementId: string | null) {
  return useQuery({
    queryKey: loanStatementsKeys.review(statementId ?? "none"),
    queryFn: () => loanStatementsApi.review(statementId!),
    enabled: statementId != null,
  });
}

/** D-9 — B3's explicit review approval (stamps the row; unlocks send). */
export function useApproveStatementReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { statementId: string; loanId: string }) =>
      loanStatementsApi.approveReview(vars.statementId),
    onSuccess: (_data, vars) => invalidateStatementScope(qc, vars.loanId),
  });
}

/** WS4 — create/replace a binding line adjustment (applied by regenerating). */
export function useUpsertStatementAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      statementId: string;
      loanId: string;
      lineKey: string;
      adjustedAmount: number;
      reason: string;
    }) =>
      loanStatementsApi.upsertAdjustment(vars.statementId, vars.lineKey, {
        adjustedAmount: vars.adjustedAmount,
        reason: vars.reason,
      }),
    onSuccess: (_data, vars) => invalidateStatementScope(qc, vars.loanId),
  });
}

/** WS4 — remove a line's adjustment (reason required). */
export function useRemoveStatementAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      statementId: string;
      loanId: string;
      lineKey: string;
      reason: string;
    }) =>
      loanStatementsApi.removeAdjustment(vars.statementId, vars.lineKey, {
        reason: vars.reason,
      }),
    onSuccess: (_data, vars) => invalidateStatementScope(qc, vars.loanId),
  });
}

/** WS5 — B3's reply on the statement's return thread. */
export function useAddStatementComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { statementId: string; loanId: string; body: string }) =>
      loanStatementsApi.addComment(vars.statementId, { body: vars.body }),
    onSuccess: (_data, vars) => invalidateStatementScope(qc, vars.loanId),
  });
}

export function useRegenerateStatementFromQueue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { statementId: string; loanId: string }) =>
      loanStatementsApi.regenerate(vars.statementId),
    onSuccess: (_data, vars) => invalidateStatementScope(qc, vars.loanId),
  });
}
